/**
 * Step 3 — Shift Planner Optimization (2-Level Architecture)
 *
 * New actions:
 *   1. fetchShiftPlannerDriverSummary — aggregated totals per driver (collapsed lanes)
 *   2. fetchShiftPlannerDriverOrders — full details for ONE driver (on expand)
 *
 * TimeBlocks have been removed — they were redundant with DriverOrders.
 * Collapsed lanes now show a visual summary bar from DriverSummary data.
 */

import { fetchGraphQLData } from 'javascript/graphql';
import store from 'javascript/store';
import { FETCH_ORDER_GROUPS } from 'javascript/containers/Dashboard/actions';
import Helpers from '@/javascript/helpers';

// ─── Action Types ───────────────────────────────────────────────────────────

export const SHIFT_PLANNER_DRIVER_SUMMARY_LOADING = 'SHIFT_PLANNER_DRIVER_SUMMARY_LOADING';
export const SHIFT_PLANNER_DRIVER_SUMMARY_SUCCESS = 'SHIFT_PLANNER_DRIVER_SUMMARY_SUCCESS';
export const SHIFT_PLANNER_DRIVER_SUMMARY_ERROR = 'SHIFT_PLANNER_DRIVER_SUMMARY_ERROR';

export const SHIFT_PLANNER_DRIVER_ORDERS_LOADING = 'SHIFT_PLANNER_DRIVER_ORDERS_LOADING';
export const SHIFT_PLANNER_DRIVER_ORDERS_SUCCESS = 'SHIFT_PLANNER_DRIVER_ORDERS_SUCCESS';
export const SHIFT_PLANNER_DRIVER_ORDERS_MORE = 'SHIFT_PLANNER_DRIVER_ORDERS_MORE';
export const SHIFT_PLANNER_DRIVER_ORDERS_ERROR = 'SHIFT_PLANNER_DRIVER_ORDERS_ERROR';
export const SHIFT_PLANNER_DRIVER_ORDERS_CLEAR = 'SHIFT_PLANNER_DRIVER_ORDERS_CLEAR';

// ─── 1. Driver Summary (totalizers, aggregated) ────────────────────────────

const DRIVER_SUMMARY_QUERY = `
  query ShiftPlannerDriverSummary($from: Int!, $to: Int!) {
    shiftPlannerDriverSummary(fromTimestamp: $from, toTimestamp: $to) {
      workerId
      totalOrders
      earliestStart
      latestEnd
      statusCounts {
        status
        count
      }
    }
  }
`;

export const fetchShiftPlannerDriverSummary =
  ({ fromTimestamp, toTimestamp }) =>
  async (dispatch) => {
    dispatch({ type: SHIFT_PLANNER_DRIVER_SUMMARY_LOADING });

    try {
      const response = await fetchGraphQLData(
        JSON.stringify({
          query: DRIVER_SUMMARY_QUERY,
          variables: { from: fromTimestamp, to: toTimestamp },
        })
      );

      // Convert array to map: { workerId: summaryObject }
      const summaryMap = {};
      response.data.shiftPlannerDriverSummary.forEach((item) => {
        summaryMap[item.workerId] = item;
      });

      dispatch({
        type: SHIFT_PLANNER_DRIVER_SUMMARY_SUCCESS,
        payload: summaryMap,
      });
    } catch (error) {
      console.error('[ShiftPlanner] Failed to fetch driver summary:', error);
      dispatch({ type: SHIFT_PLANNER_DRIVER_SUMMARY_ERROR, error });
    }
  };

// ─── 2. Driver Orders (full details, paginated, on expand) ─────────────────

const DRIVER_ORDERS_QUERY = `
  query ShiftPlannerDriverOrders($driverId: ID!, $from: Int!, $to: Int!, $limit: Int!, $offset: Int!) {
    shiftPlannerDriverOrders(
      driverId: $driverId,
      fromTimestamp: $from,
      toTimestamp: $to,
      limit: $limit,
      offset: $offset
    ) {
      collection {
        id
        type
        status
        driverAcceptance
        orderId
        ticketId
        plannedAt
        startAt
        toStartAt
        endAt
        completeOn
        fuelingDuration
        customerName
        shipToName
        address
        hubName
        carrierName
        deliveryTruckNames
        linkedOrders
        suppliers
        preferredSuppliers
        driverId
        driverName
        customerBranchSchedule{
          availability{
            day
            startTime
            endTime
            bestStartTime
            bestEndTime
          }
        }
        customerSchedule{
          availability{
            day
            startTime
            endTime
            bestStartTime
            bestEndTime
          }
        }
      }
      metadata {
        totalCount
        hasMore
      }
    }
  }
`;

const DRIVER_ORDERS_PAGE_SIZE = 50;

/**
 * Fetch the first page of orders for a driver (called on lane expand).
 */
export const fetchShiftPlannerDriverOrders =
  ({ driverId, fromTimestamp, toTimestamp }) =>
  async (dispatch) => {
    dispatch({ type: SHIFT_PLANNER_DRIVER_ORDERS_LOADING, driverId });

    try {
      const response = await fetchGraphQLData(
        JSON.stringify({
          query: DRIVER_ORDERS_QUERY,
          variables: {
            driverId,
            from: fromTimestamp,
            to: toTimestamp,
            limit: DRIVER_ORDERS_PAGE_SIZE,
            offset: 0,
          },
        })
      );

      const { collection, metadata } = response.data.shiftPlannerDriverOrders;

      store.dispatch({
        type: FETCH_ORDER_GROUPS,
        payload: Helpers.convertArrayToHash(collection, 'orderGroups'),
      });

      dispatch({
        type: SHIFT_PLANNER_DRIVER_ORDERS_SUCCESS,
        driverId,
        payload: {
          orders: collection,
          totalCount: metadata.totalCount,
          hasMore: metadata.hasMore,
          offset: DRIVER_ORDERS_PAGE_SIZE,
        },
      });
    } catch (error) {
      console.error(`[ShiftPlanner] Failed to fetch orders for driver ${driverId}:`, error);
      dispatch({ type: SHIFT_PLANNER_DRIVER_ORDERS_ERROR, driverId, error });
    }
  };

/**
 * Fetch the NEXT page of orders for a driver (infinite scroll).
 * Appends to existing orders instead of replacing them.
 */
export const fetchMoreDriverOrders =
  ({ driverId, fromTimestamp, toTimestamp, currentOffset }) =>
  async (dispatch) => {
    try {
      const response = await fetchGraphQLData(
        JSON.stringify({
          query: DRIVER_ORDERS_QUERY,
          variables: {
            driverId,
            from: fromTimestamp,
            to: toTimestamp,
            limit: DRIVER_ORDERS_PAGE_SIZE,
            offset: currentOffset,
          },
        })
      );

      const { collection, metadata } = response.data.shiftPlannerDriverOrders;

      dispatch({
        type: SHIFT_PLANNER_DRIVER_ORDERS_MORE,
        driverId,
        payload: {
          orders: collection,
          totalCount: metadata.totalCount,
          hasMore: metadata.hasMore,
          offset: currentOffset + DRIVER_ORDERS_PAGE_SIZE,
        },
      });
    } catch (error) {
      console.error(`[ShiftPlanner] Failed to fetch more orders for driver ${driverId}:`, error);
    }
  };

/**
 * Clear loaded orders for a driver (called on lane collapse).
 * Frees memory for drivers that are no longer visible.
 */
export const clearDriverOrders = (driverId) => ({
  type: SHIFT_PLANNER_DRIVER_ORDERS_CLEAR,
  driverId,
});
